import React from 'react'
import { Button, Typography } from 'antd'
import { useIntl } from 'react-intl'

import capitalizeString from '@vacationtracker/shared/functions/capitalize-string'

import IntlMessages from '../../../../util/IntlMessages'
import { Banner } from '../../../../design-system/Banner'
import { isConnectPlatform } from '../outcomes'
import { PLATFORM_BRAND } from '../platform-brand'
import { ConnectPlatform, SignupBanner } from '../types'

const { Link, Text } = Typography

export interface ISignupBannerProps {
  banner: SignupBanner
  onLogin: () => void
  onResetPassword: () => void
  /** Takes the platform the backend named, which can be one this page cannot start. */
  onLoginWithPlatform: (platform: string) => void
  /** Asks the team's admins to add the visitor, with the provider sign-in that found the team. */
  onRequestAccess: (params: { platform: ConnectPlatform; email: string }) => void
  /** Held while a request is on its way: each click notifies the admins. */
  isRequestingAccess?: boolean
  onContactSupport: () => void
}

const SignupBannerView = ({
  banner,
  onLogin,
  onResetPassword,
  onLoginWithPlatform,
  onRequestAccess,
  isRequestingAccess = false,
  onContactSupport,
}: ISignupBannerProps): React.ReactElement | null => {
  const { formatMessage, formatDate } = useIntl()

  if (banner.kind === 'none') return null

  const wrap = (content: React.ReactElement) => (
    <div className="signup-banner" data-qa="signup-banner" data-banner-kind={banner.kind}>
      {content}
    </div>
  )

  switch (banner.kind) {
    // The one banner with somewhere to go: this address already has an account, so both of the
    // ways back into it sit inside the message rather than in the single action slot.
    case 'alreadySignedUp':
      return wrap(
        <Banner
          tone="info"
          message={formatMessage({ id: 'error.signup.alreadySignedUp.title' })}
          description={
            <>
              <div>
                {formatMessage(
                  { id: 'error.signup.alreadySignedUp.descriptionLine1' },
                  {
                    email: banner.email,
                    text: (chunks: React.ReactNode) => <Text keyboard>{chunks}</Text>,
                  }
                )}
              </div>
              <Link onClick={onLogin} data-qa="signup-login-link">
                <IntlMessages id="app.login" />
              </Link>
              {' · '}
              <Link onClick={onResetPassword} data-qa="signup-reset-password-link">
                <IntlMessages id="app.resetPassword" />
              </Link>
            </>
          }
        />
      )

    case 'invalidEmail':
      return wrap(<Banner tone="warning" message={formatMessage({ id: 'error.emailError' })} description={formatMessage({ id: 'form.validEmailRequired' })} />)

    case 'disposableEmail':
      return wrap(<Banner tone="warning" message={formatMessage({ id: 'error.emailError' })} description={formatMessage({ id: 'error.emailError.disposable' })} />)

    // Replaced a corner toast: someone coming back from the provider tab finds the answer on
    // the page rather than gone from a corner they never looked at.
    case 'signupCancelled':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'signup.signUpCancelledTitle' })}
          description={formatMessage({ id: 'signup.signUpCancelledDescription' }, { platform: banner.platform })}
        />
      )

    // Only said once the tab is cut off, when this page cannot know whether it is open, so it
    // says what holds either way rather than calling the attempt cancelled.
    case 'continueInPlatformTab':
      return wrap(
        <Banner
          tone="info"
          message={formatMessage({ id: 'signup.continueInPlatformTab.title' }, { platform: banner.platform })}
          description={formatMessage({ id: 'signup.continueInPlatformTab.description' }, { platform: banner.platform })}
        />
      )

    // Cognito refuses a withdrawn account and a token it no longer honours with the same
    // "Incorrect username or password", so this says what is true of both. Unlike sign-in, it
    // also has to say that the organization the visitor came to create was not created.
    case 'platformSigninRejected':
      return wrap(
        <Banner
          tone="danger"
          live="assertive"
          message={formatMessage({ id: 'signup.existingAccountSigninRejected.title' }, { platform: banner.platform })}
          description={formatMessage({ id: 'signup.existingAccountSigninRejected.description' })}
        />
      )

    case 'subscriptionExpired':
      return wrap(<Banner tone="warning" message={formatMessage({ id: 'connect.subscriptionExpiredTitle' })} description={formatMessage({ id: 'error.subscriptionExpired' })} />)

    case 'orgOnOtherPlatform': {
      const { platform, organizationName } = banner
      const platformName = isConnectPlatform(platform) ? PLATFORM_BRAND[platform].short : capitalizeString(platform)
      return wrap(
        <Banner
          tone="info"
          message={formatMessage({ id: 'connect.youAreAlreadyMember' }, { existingOrganizationName: organizationName })}
          description={
            <>
              <div>{formatMessage({ id: 'signin.platformStep.orgUsesPlatform' }, { organizationName, platform: platformName })}</div>
              <Link onClick={() => onLoginWithPlatform(platform)} data-qa="signup-login-with-platform-link">
                {formatMessage({ id: 'signin.signInWithPlatform' }, { platform: platformName })}
              </Link>
              {' · '}
              <Link onClick={onContactSupport} data-qa="signup-contact-support-link">
                <IntlMessages id="app.contactSupport" />
              </Link>
            </>
          }
        />
      )
    }

    // The provider sign-in that got here proved the visitor belongs to the team, which is what
    // makes it safe to say the team is here, and what the ask is sent with.
    case 'companyExists': {
      const { platform, organizationName, email } = banner
      return wrap(
        <Banner
          tone="info"
          message={organizationName ? formatMessage({ id: 'signup.companyExists.titleNamed' }, { organizationName }) : formatMessage({ id: 'signup.companyExists.title' })}
          description={
            <>
              <div>{formatMessage({ id: 'signup.companyExists.description' })}</div>
              <div className="signup-banner-action">
                <Button
                  type="link"
                  size="small"
                  onClick={() => onRequestAccess({ platform, email })}
                  disabled={isRequestingAccess}
                  loading={isRequestingAccess}
                  data-qa="signup-request-access"
                >
                  {formatMessage({ id: 'signin.requestAccess' })}
                </Button>
              </div>
            </>
          }
        />
      )
    }

    case 'accessRequested':
      return wrap(<Banner tone="success" message={formatMessage({ id: 'signup.requestAccessSent' })} />)

    // Asking again before this date reaches nobody, so the page says when it is worth doing.
    case 'accessAlreadyRequested':
      return wrap(<Banner tone="info" message={formatMessage({ id: 'signin.requestAccessAlreadySent' }, { date: formatDate(new Date(banner.retryOn), { dateStyle: 'long' }) })} />)

    case 'accessRequestFailed':
      return wrap(<Banner tone="danger" live="assertive" message={formatMessage({ id: 'signin.requestAccessFailed' })} />)

    case 'notWorkspaceAccount':
      return wrap(
        <Banner
          tone="warning"
          message={formatMessage({ id: 'connect.google.notWorkspaceUserTitle' })}
          description={formatMessage({ id: 'connect.google.notWorkspaceUserDescription' })}
        />
      )

    default:
      return wrap(<Banner tone="danger" live="assertive" message={formatMessage({ id: 'signup.signUpFailed' })} />)
  }
}

export default SignupBannerView
